import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';

import { DataTable } from '@unified-checkout/design-system';
import { Store } from '@unified-checkout/design-system/icons/index';
import type { IColumnsMap } from 'libs/design-system/src/types/data_table';

import { MultiSectionCard } from '@app/dashboard/components/inner_layout/MultiSectionCard';
import { getBillsHistory } from '@app/dashboard/services/inner_layout/bills/get-bills-history';
import { getDistributorHistory } from '@app/dashboard/services/inner_layout/bills/get-bills-distributor-history';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  ALL_SERVICES_ID,
  BILLS_HISTORY_TAB_ID,
  BILLS_DISTRIBUTOR_HISTORY_TAB_ID,
  SAVED_BILLS_TAB_ID,
  SAVED_MERCHANTS_TAB_ID,
  TABLE_ID
} from '@app/dashboard/constants/Bills';
import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS
} from '@app/dashboard/constants/tracking_events/bills';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useLangStore } from '@app/dashboard/store/lang';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';

import type { ITapContent } from '@app/dashboard/types/innerLayout/bills/tap-content';

import { AllServices } from './components/AllServices';
import { SavedMerchantCard } from './components/SavedMerchantCard';
import { SavedBills } from './components/SavedBills';

import styles from './bills.module.scss';

export const BillsTabContent = ({
  activeTab,
  savedMerchants,
  isSavedMerchantsPending,
  refetchSavedMerchants,
  billsData,
  isBillsLoading,
}: ITapContent) => {
  const { isLive } = useTestLiveModeStore();
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const navigate = useNavigate();

  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const distributorQueryKeys = useMemo(
    () => [QUERY_KEYS.GET_BILLS_DISTRIBUTOR_HISTORY],
    []
  );

  const historyQueryKeys = useMemo(
    () => [QUERY_KEYS.GET_BILLS_HISTORY],
    []
  );

  const handleTableRowClick = (bill: any) => {
    amplitude.track(BILLS_EVENTS.BILLS_HISTORY_BILL_SELECTED, {
      [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: bill?.category_title,
      [BILLS_EVENTS_PARAMS.TOTAL_AMOUNT]: bill?.amount,
      [BILLS_EVENTS_PARAMS.TRANSACTION_ID]: bill?.id,
    });
    navigate(INNER_ROUTES.SINGLE_BILL.replace(':type',bill.bill_type).replace(':id', bill.bill_id));
  };

  const distributorColumnsMap: IColumnsMap = [
    {
      id: 'type',
      name: t('innerLayout.billsPage.table.headers.history.type'),
      type: 'custom',
      key: "name",
      render: (value, fullData) => <MultiSectionCard title={value} subtitle={`MID: ${fullData?.merchant_id}`} icon={Store} />
    },
    {
      id: 'date',
      name: t('innerLayout.billsPage.table.headers.history.date'),
      type: 'date',
      key: "date"
    },
    {
      id: 'Amount',
      name: t('innerLayout.billsPage.table.headers.history.amount'),
      type: 'text',
      key: "amount",
      additionalText: { key: "currency", placement: lang === 'en' ? "start" : "end" }
    },
    {
      id: 'Status',
      name: t('innerLayout.billsPage.table.headers.history.status'),
      type: 'badge',
      key: "status",
      labelComparingValue: 'status',
      label: {
        [t('innerLayout.billsPage.table.body.status.success')]: { color: 'green' },
        [t('innerLayout.billsPage.table.body.status.pending')]: { color: 'yellow' },
        [t('innerLayout.billsPage.table.body.status.failed') || "Fail"]: { color: 'red' },
      },
    },
  ];

  const columnsMap: IColumnsMap = [
    {
      id: 'type',
      name: t('innerLayout.billsPage.table.headers.history.type'),
      type: 'custom',
      key: "name",
      render: (value, fullData) =>
        <MultiSectionCard
          title={fullData?.category_title}
          subtitle={fullData?.id}
          icon={fullData?.category_icon} />
    },
    {
      id: 'date',
      name: t('innerLayout.billsPage.table.headers.history.date'),
      type: 'date',
      key: "date"
    },
    {
      id: 'Amount',
      name: t('innerLayout.billsPage.table.headers.history.amount'),
      type: 'text',
      key: "amount",
      additionalText: { key: "currency", placement: lang === 'en' ? "start" : "end" }
    },
    {
      id: 'Status',
      name: t('innerLayout.billsPage.table.headers.history.status'),
      type: 'badge',
      key: "status",
      labelComparingValue: 'status',
      label: {
        [t('innerLayout.billsPage.table.body.status.success')]: { color: 'green' },
        [t('innerLayout.billsPage.table.body.status.pendingStatus')]: { color: 'yellow' },
        [t('innerLayout.billsPage.table.body.status.fail')]: { color: 'red' },
      },
    },
  ];

  switch (activeTab) {
    case ALL_SERVICES_ID:
      return <AllServices billsData={billsData} isPending={isBillsLoading} />;

    case BILLS_DISTRIBUTOR_HISTORY_TAB_ID:
      return (
        <DataTable
          lang={lang}
          tableBody={{
            lang,
            tableId: TABLE_ID,
            dataLocation: 'data.data.results',
            columnsMap: distributorColumnsMap,
          }}
          pagination={{
            hideLabels: isMobile,
            type: 'page_based',
            dataLocation: {
              totalItems: 'data.data.count'
            },
            isMobile,
            maxVisiblePages: 6,
          }}
          queries={{
            queryKeys: distributorQueryKeys,
            queryFunction: getDistributorHistory,
          }}
          emptyState={{
            text: t('innerLayout.transactionsPage.table.body.noTransactionsYet'),
            description: t('innerLayout.transactionsPage.table.body.noTransactionsHint')
          }}
        />
      );

    case BILLS_HISTORY_TAB_ID:
      return (
        <DataTable
          lang={lang}
          tableBody={{
            lang,
            tableId: TABLE_ID,
            dataLocation: 'data.data.results',
            columnsMap,
            rowClickHandler: handleTableRowClick
          }}
          pagination={{
            hideLabels: isMobile,
            type: 'cursor_based',
            dataLocation: {
              next: 'data.data.next',
              prev: 'data.data.previous',
            },
            isMobile,
          }}
          queries={{
            queryKeys: historyQueryKeys,
            queryFunction: getBillsHistory,
            onResponseChange: (response: any) => {
              amplitude.track(BILLS_EVENTS.VIEW_BILLS_HISTORY, {
                [BILLS_EVENTS_PARAMS.STATUS]: response?.data?.status,
                [BILLS_EVENTS_PARAMS.ERROR]: response?.response?.data?.message,
              });
            }
          }}
          emptyState={{
            text: t('innerLayout.transactionsPage.table.body.noTransactionsYet'),
            description: t('innerLayout.transactionsPage.table.body.noTransactionsHint')
          }}
        />
      );

    case SAVED_MERCHANTS_TAB_ID:
      return (
        <SavedMerchantCard
          savedMerchants={savedMerchants}
          isSavedMerchantsPending={isSavedMerchantsPending}
          refetch={refetchSavedMerchants}
        />
      );

    case SAVED_BILLS_TAB_ID:
      return (
        <SavedBills
          billsData={billsData}
        />
      )

    default:
      return null;
  }
};